import {useMemo} from 'react';
import {
  useSamDefinitionSize,
  useSamDefinitionWithinLimits,
} from '../../hooks/api/samDefinition';
import {useSamEditorStore} from './useSamEditorStore';
import _ from 'lodash';
import {useIsFreemiumCustomer} from '../../hooks/api/metadata';
import {useDebounce} from 'usehooks-ts';

const useDebouncedServerState = () => {
  const {toServerState} = useSamEditorStore();

  // Memoize the server state to maintain a stable reference
  const serverState = useMemo(() => {
    return toServerState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(toServerState())]);

  const debouncedServerState = useDebounce(serverState, 250);

  return {
    debouncePending: !_.isEqual(serverState, debouncedServerState),
    debouncedServerState,
  };
};

export const useSamDefinitionLimits = () => {
  const {isEmpty} = useSamEditorStore();
  const isFreemiumCustomer = useIsFreemiumCustomer();

  const {debouncedServerState, debouncePending} = useDebouncedServerState();

  const samDefinitionWithinLimits = useSamDefinitionWithinLimits(
    {
      samDefinition: debouncedServerState,
    },
    {enabled: isFreemiumCustomer && !isEmpty()}
  );

  const refetchPending = isFreemiumCustomer && debouncePending;

  return {
    withinLimits:
      !isFreemiumCustomer || isEmpty() ? true : samDefinitionWithinLimits.data,
    isFetching: refetchPending || samDefinitionWithinLimits.isFetching,
    isInitialLoading: samDefinitionWithinLimits.isLoading,
  };
};

export const useDebouncedSamSize = () => {
  const {debouncedServerState, debouncePending} = useDebouncedServerState();
  const samDefinitionSize = useSamDefinitionSize({
    samDefinition: debouncedServerState,
  });

  return {
    size: samDefinitionSize.data,
    isFetching: debouncePending || samDefinitionSize.isFetching,
    isLoading: samDefinitionSize.isLoading,
  };
};
